/**
 * ActionPicker Component
 * Dialog for selecting an action from the device's actions list.
 */
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Skeleton,
  InputAdornment,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  ImageOutlined as ImageIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Action } from '../../types/action';

interface ActionPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (action: Action) => void;
  actions: Action[];
  loading?: boolean;
  title?: string;
}

export const ActionPicker: React.FC<ActionPickerProps> = ({
  open,
  onClose,
  onSelect,
  actions,
  loading = false,
  title,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter actions by search query
  const filteredActions = useMemo(() => {
    if (!searchQuery.trim()) return actions;
    const query = searchQuery.toLowerCase();
    return actions.filter(
      (action) =>
        action.name.toLowerCase().includes(query) ||
        action.description.toLowerCase().includes(query)
    );
  }, [actions, searchQuery]);

  const handleSelect = (action: Action) => {
    onSelect(action);
    setSearchQuery('');
    onClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title || t('schedules.steps.selectAction')}</DialogTitle>
      <DialogContent>
        {/* Search field */}
        <TextField
          fullWidth
          placeholder="Search actions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          autoFocus
        />

        {/* Loading state */}
        {loading && (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={i}>
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Empty state */}
        {!loading && actions.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('actions.noActionsYet')}
            </Typography>
          </Box>
        )}

        {/* No matches */}
        {!loading && actions.length > 0 && filteredActions.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No actions match "{searchQuery}"
            </Typography>
          </Box>
        )}

        {/* Actions grid */}
        {!loading && filteredActions.length > 0 && (
          <Grid container spacing={2}>
            {filteredActions.map((action) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={action.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleSelect(action)}
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    {/* Image */}
                    {action.thumbnailUrl || action.pictureUrl ? (
                      <CardMedia
                        component="img"
                        height="100"
                        image={action.thumbnailUrl || action.pictureUrl || ''}
                        alt={action.name}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 100,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.100',
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                      </Box>
                    )}

                    {/* Content */}
                    <CardContent sx={{ flexGrow: 1, py: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                        title={action.name}
                      >
                        {action.name}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};
